import { createContext, useContext } from 'react';

import { hideDrift } from './hooks/useDrift';

// enum of possible steps, this is to prevent possible typos
export const stepsEnum = {
  PAUSE_CANCEL: 'PAUSE_CANCEL',
  PAUSE_REASON: 'PAUSE_REASON',
  PAUSE: 'PAUSE',
  CANCEL_REASON: 'CANCEL_REASON',
  CANCEL_REASON_2: 'CANCEL_REASON_2',
  OFFER_PROMO: 'OFFER_PROMO',
  OFFER_PROMO2: 'OFFER_PROMO2',
  CANCEL: 'CANCEL',
  CANCEL_CONFIRM: 'CANCEL_CONFIRM',
  DOWNGRADE_COMPARE: 'DOWNGRADE_COMPARE',
};

export const stepToLabel = (currentStep, cancelOther) => {
  let trackActionText;

  switch (currentStep) {
    case stepsEnum.PAUSE_CANCEL:
      trackActionText = 'cancel step 1>';
      break;
    case stepsEnum.CANCEL_REASON:
      trackActionText = 'cancel step 2>';
      break;
    case stepsEnum.CANCEL_REASON_2:
      trackActionText = 'cancel step 3>';
      break;
    case stepsEnum.OFFER_PROMO:
      trackActionText = 'cancel step 3>';
      break;
    case stepsEnum.OFFER_PROMO2:
      trackActionText = 'cancel step 3>';
      break;
    case stepsEnum.CANCEL:
      trackActionText = 'cancel details step>';
      break;
    case stepsEnum.PAUSE_REASON:
      trackActionText = 'pause account step 1>';
      break;
    case stepsEnum.PAUSE:
      trackActionText = 'pause details step>';
      break;
    case stepsEnum.CANCEL_CONFIRM:
      trackActionText = 'cancel confirm>';
      break;
    case stepsEnum.DOWNGRADE_COMPARE:
      trackActionText = 'downgrade compare>';
      break;
    default:
      trackActionText = 'Unknown cancel flow page';
      break;
  }

  if (cancelOther) {
    trackActionText += `${currentStep === stepsEnum.OFFER_PROMO ? 'other promo step ' : ''}${
      currentStep === stepsEnum.OFFER_PROMO2 ? 'other pause offer step ' : ''
    }`;
  } else {
    trackActionText += `${currentStep === stepsEnum.OFFER_PROMO ? 'support offer ' : ''}${
      currentStep === stepsEnum.OFFER_PROMO2 ? 'promo step ' : ''
    }`;
  }

  return trackActionText;
};

export const initialCancelFlowState = {
  testVal: 'from context!',
  isOpen: false,
  showFooter: false,
  footerContinueDisabled: false,
  mustMakeSelection: false,
  isPausing: false,
  isPaused: false,
  isCanceling: false,
  isCanceled: false,
  isDowngrading: false,
  isPremium: false,
  idDowngraded: true,
  cancelReason: null,
  cancelReason2: null,
  pauseReason: null, // TODO: the backend doesn't currently record this (or positiveReason)
  positiveReason: null, // combining the optional reason from both pause/cancel
  currencyCode: null,
  pauseCost: null,
  savingsAmount: null,
  daysLeftInBillingPeriod: null,
  nextBillingDate: null,
  estimationDate: null,
  currentPrice: 0,
  hasNotRegistered: null,
  planType: null,
  newPrice: 0,
  currentStep: stepsEnum.PAUSE_CANCEL,
  history: [],
  cancelReasons: [],
  cancelReasons2: [
    { id: '1', value: '1', description: 'cancel-flow.steps.cancel-feedback-2.cancel-reason2.option1' },
    { id: '2', value: '2', description: 'cancel-flow.steps.cancel-feedback-2.cancel-reason2.option2' },
    { id: '3', value: '3', description: 'cancel-flow.steps.cancel-feedback-2.cancel-reason2.option3' },
    { id: '4', value: '4', description: 'cancel-flow.steps.cancel-feedback-2.cancel-reason2.option4' },
  ],
  pauseReasons: [
    { id: '5', value: '5', description: 'cancel-flow.steps.pause-feedback.reason.not-useful' },
    { id: '4', value: '4', description: 'cancel-flow.steps.pause-feedback.reason.seasonal-user' },
    { id: '13', value: '13', description: 'cancel-flow.steps.pause-feedback.reason.missing-features' },
    { id: '11', value: '11', description: 'cancel-flow.steps.pause-feedback.reason.other' },
  ],
  cancelSuccessCallback: () => {},
  promoData: {
    isPromoOffered: null,
    isPromoApplied: null,
    promoError: null,
    content: {
      promoHeaderText: null,
      promoButtonText: null,
      promoCode: null,
      username: null,
    },
  },
  promoDataV2: {
    isPromoOffered: null,
    isPromoApplied: null,
    promoError: null,
    content: {
      promoHeaderText: null,
      promoButtonText: null,
      promoCode: null,
      username: null,
    },
  },
  cancelOfferValues: null,
  cancelStepsCount: 4,
  hasSMSAddOn: null,
};

// the actual step structure
// the 'forward' action in the reducer will take the current step
// and use it with this to see what the next step is
export const steps = {
  PAUSE_CANCEL: [stepsEnum.PAUSE_REASON, stepsEnum.CANCEL_REASON, stepsEnum.DOWNGRADE_COMPARE],
  PAUSE_REASON: [stepsEnum.PAUSE],
  CANCEL_REASON: [stepsEnum.OFFER_PROMO],
  // turn off CANCEL_REASON_2 for now
  // CANCEL_REASON: [stepsEnum.CANCEL_REASON_2],
  // CANCEL_REASON_2: [stepsEnum.OFFER_PROMO],
  OFFER_PROMO: [stepsEnum.OFFER_PROMO2],
  OFFER_PROMO2: [stepsEnum.CANCEL],
  CANCEL: [stepsEnum.CANCEL_CONFIRM],
};

const setShowFooter = (currentStep, isOpen) => {
  if (!isOpen) return false;

  switch (currentStep) {
    case stepsEnum.PAUSE_CANCEL:
      return false;
    default:
      return true;
  }
};

export const cancelFlowReducer = (state = initialCancelFlowState, action) => {
  const shouldDisableContinue = (nextStep) => {
    let localFooterContinueDisabled = state.footerContinueDisabled;

    if (
      (nextStep === stepsEnum.CANCEL_REASON && !state.cancelReason) ||
      (nextStep === stepsEnum.PAUSE_REASON && !state.pauseReason)
    ) {
      localFooterContinueDisabled = true;
    }

    return localFooterContinueDisabled;
  };

  switch (action.type) {
    case 'open': {
      return {
        ...state,
        currentStep: stepsEnum.PAUSE_CANCEL,
        isOpen: true,
      };
    }
    case 'close': {
      hideDrift();

      return {
        ...state,
        currentStep: state.currentStep, // avoid flash of the first page when closing
        cancelReasons: state.cancelReasons,
        isOpen: false,
        showFooter: false,
      };
    }
    case 'forward': {
      let step = 0;

      if (action.value) step = Number(action.value);

      const nextStep = steps[state.currentStep][step];
      const showFooter = setShowFooter(nextStep, state.isOpen);

      return {
        ...state,
        // if replace is true, replace current step without adding it to the history
        history: action.replace ? [...state.history] : [...state.history, state.currentStep],
        currentStep: nextStep,
        footerContinueDisabled: shouldDisableContinue(nextStep),
        mustMakeSelection: false,
        showFooter,
      };
    }
    case 'back': {
      const lastIndex = state.history.length - 1;
      const previousStep = state.history[lastIndex];
      const showFooter = setShowFooter(previousStep, state.isOpen);

      return {
        ...state,
        currentStep: previousStep,
        history: [...state.history.slice(0, lastIndex)],
        footerContinueDisabled: false,
        showFooter,
        mustMakeSelection: false,
      };
    }
    case 'jump': {
      const nextStep = action.value;
      const showFooter = setShowFooter(nextStep, state.isOpen);

      return {
        ...state,
        history: [...state.history, state.currentStep],
        footerContinueDisabled: shouldDisableContinue(nextStep),
        currentStep: nextStep,
        showFooter,
      };
    }
    case 'setPausing': {
      return {
        ...state,
        isPausing: true,
      };
    }
    case 'setPaused': {
      return {
        ...state,
        isPausing: false,
        isPaused: action.value,
      };
    }
    case 'setHasSMSAddOn': {
      return {
        ...state,
        hasSMSAddOn: action.value,
      };
    }
    case 'setCanceling': {
      return {
        ...state,
        isCanceling: true,
      };
    }
    case 'setCurrencyCode': {
      return {
        ...state,
        currencyCode: action.value,
      };
    }
    case 'setCanceled': {
      return {
        ...state,
        isCanceling: false,
        isCanceled: action.value,
      };
    }
    case 'setDowngrading': {
      return {
        ...state,
        isDowngrading: true,
      };
    }
    case 'setDowngraded': {
      return {
        ...state,
        isDowngrading: false,
        isDowngraded: action.value,
      };
    }
    case 'setIsPremium': {
      return {
        ...state,
        isPremium: action.value,
      };
    }
    case 'setShowFooter': {
      return {
        ...state,
        showFooter: action.value,
      };
    }
    case 'setPauseCost': {
      return {
        ...state,
        pauseCost: action.value,
      };
    }
    case 'setSavingsAmount': {
      return {
        ...state,
        savingsAmount: action.value,
      };
    }
    case 'setEstimationDate': {
      return {
        ...state,
        estimationDate: action.value,
      };
    }
    case 'setCurrentPrice': {
      return {
        ...state,
        currentPrice: action.value,
      };
    }
    case 'setNewPrice': {
      return {
        ...state,
        newPrice: action.value,
      };
    }
    case 'setDaysLeftInBillingPeriod': {
      return {
        ...state,
        daysLeftInBillingPeriod: action.value,
      };
    }
    case 'setNextBillingDate': {
      return {
        ...state,
        nextBillingDate: action.value,
      };
    }
    case 'setPromoData': {
      return {
        ...state,
        promoData: {
          ...state.promoData,
          isPromoOffered: action.isPromoOffered,
          content: action.content,
        },
      };
    }
    case 'setPromoDataV2': {
      return {
        ...state,
        promoDataV2: {
          ...state.promoDataV2,
          isPromoOffered: action.isPromoOffered,
          content: action.content,
        },
      };
    }
    case 'setCancelOfferValues': {
      return {
        ...state,
        cancelOfferValues: action.value,
      };
    }
    case 'setPromoApplied': {
      return {
        ...state,
        promoData: {
          ...state.promoData,
          isPromoApplied: action.value,
        },
      };
    }
    case 'setPromoError': {
      return {
        ...state,
        promoData: {
          ...state.promoData,
          promoError: action.value,
        },
      };
    }
    case 'setCancelReason': {
      return {
        ...state,
        cancelReason: action.value,
        footerContinueDisabled: false,
      };
    }
    case 'setCancelReason2': {
      return {
        ...state,
        cancelReason2: action.value,
        footerContinueDisabled: false,
      };
    }
    case 'setCancelReasons': {
      return {
        ...state,
        cancelReasons: action.value,
      };
    }
    case 'setCancelReasons2': {
      return {
        ...state,
        cancelReasons2: action.value,
      };
    }
    case 'setPauseReason': {
      return {
        ...state,
        pauseReason: action.value,
        footerContinueDisabled: false,
      };
    }
    case 'setMustMakeSelection': {
      return {
        ...state,
        mustMakeSelection: action.value,
      };
    }
    case 'setHasNotRegistered': {
      return {
        ...state,
        hasNotRegistered: action.value,
      };
    }
    case 'setPlanType': {
      return {
        ...state,
        planType: action.value,
      };
    }
    case 'removePromoStep': {
      if (steps.OFFER_PROMO[0] === stepsEnum.CANCEL) {
        // promo 2 is already removed, skip both offers
        steps.CANCEL_REASON = [stepsEnum.CANCEL];
      } else {
        steps.CANCEL_REASON = [stepsEnum.OFFER_PROMO2];
      }

      return {
        ...state,
        cancelStepsCount: state.cancelStepsCount - 1,
      };
    }
    case 'removePromoStepV2': {
      if (steps.CANCEL_REASON[0] === stepsEnum.OFFER_PROMO2) {
        // promo 1 is already removed, skip both offers
        steps.CANCEL_REASON = [stepsEnum.CANCEL];
      } else {
        steps.OFFER_PROMO = [stepsEnum.CANCEL];
      }

      return {
        ...state,
        cancelStepsCount: state.cancelStepsCount - 1,
      };
    }
    default:
      return state;
  }
};

export const CancelFlowContext = createContext();

export const useCancelFlowContext = () => useContext(CancelFlowContext);
